import { ReactNode, useState } from 'react'

import type { ColorScheme } from '@hivewatch/dark'
import { Box, CheckboxFilterList, MultiSelectInput } from '@hivewatch/dark'

import { FilterOptionI } from '@/utils/filterDrawers'

import { FilterRow } from './FilterRow'

export interface MultiSelectFilterRowIProps<T> {
  label: string
  /** Trigger placeholder AND the panel's search placeholder (parity with the
   * legacy control, which used one string for both). */
  placeholder: string
  colorScheme: ColorScheme
  options: FilterOptionI<T>[] | undefined
  value: FilterOptionI<T>[] | null | undefined
  onChange: (value: FilterOptionI<T>[] | null) => void
  /**
   * Stable string key per option for the checkbox list and chips. Defaults to
   * `String(option.value)`; rows whose values are objects (e.g. Priority)
   * must key by label instead.
   */
  getOptionKey?: (option: FilterOptionI<T>) => string
  /** Server-side search wiring (Facility, Device). When present the panel
   * filters externally; otherwise it filters the option labels internally. */
  searchValue?: string | null
  onSearchChange?: ((value: string) => void) | null
  isLoading?: boolean | null
  /** Optional element rendered next to the label (e.g. an info button). */
  labelAction?: ReactNode
  /** Empty-state message for the option panel — e.g. a "type at least N
   * characters" hint. Receives the active search text. */
  noOptionsMessage?: (obj: { inputValue: string }) => string | null
  /** Applied to a wrapper element — MultiSelectInput has no data-testid prop. */
  'data-testid'?: string
  /** Pass for rows whose `options` already come in a meaningful order (e.g.
   * facility search's name/short-name-before-address ranking) — otherwise
   * the shared list alphabetizes by label, which is what most rows want. */
  preserveOptionOrder?: boolean
}

/**
 * Multi-select filter row on the dark MultiSelectInput + CheckboxFilterList.
 * Shared across the dark page filter drawers — see `./FilterDrawer`.
 */
export const MultiSelectFilterRow = <T,>({
  label,
  placeholder,
  colorScheme,
  options,
  value,
  onChange,
  getOptionKey,
  searchValue,
  onSearchChange,
  isLoading,
  labelAction,
  noOptionsMessage,
  'data-testid': dataTestId,
  preserveOptionOrder,
}: MultiSelectFilterRowIProps<T>) => {
  const getKey =
    getOptionKey ?? ((option: FilterOptionI<T>) => String(option.value))
  const selected = value ?? []
  const selectedKeys = selected.map(getKey)
  const rowOptions = options ?? []

  const [internalSearch, setInternalSearch] = useState('')
  const isExternalSearch = typeof onSearchChange === 'function'
  const activeSearchValue = isExternalSearch
    ? searchValue ?? ''
    : internalSearch
  const handleSearchChange = isExternalSearch
    ? onSearchChange
    : setInternalSearch

  // Selected options merged in so chip labels always resolve, even when the
  // current options list has been narrowed by a server-side search.
  const optionByKey = new Map<string, FilterOptionI<T>>()
  rowOptions.forEach((option) => optionByKey.set(getKey(option), option))
  selected.forEach((option) => {
    const key = getKey(option)
    if (!optionByKey.has(key)) optionByKey.set(key, option)
  })

  const chipOptions = Array.from(optionByKey.entries()).map(
    ([key, option]) => ({ value: key, label: option.label })
  )

  const listOptions = rowOptions.map((option) => ({
    value: getKey(option),
    label: option.label,
    subtitle: option.subtitle,
    caption: option.caption,
  }))

  const emitChange = (next: FilterOptionI<T>[]) => {
    onChange(next.length > 0 ? next : null)
  }

  const handleToggle = (key: string) => {
    if (selectedKeys.includes(key)) {
      emitChange(selected.filter((option) => getKey(option) !== key))
      return
    }
    const option = optionByKey.get(key)
    if (option) emitChange([...selected, option])
  }

  const handleSelectAll = () => {
    // Every listed option plus any existing selection the list doesn't show
    emitChange(Array.from(optionByKey.values()))
  }

  const noOptionsText =
    noOptionsMessage?.({ inputValue: activeSearchValue }) ?? undefined

  return (
    <FilterRow
      colorScheme={colorScheme}
      label={label}
      labelAction={labelAction}
    >
      {/* MultiSelectInput has no data-testid prop; carry it on a wrapper. */}
      <Box data-testid={dataTestId}>
        <MultiSelectInput
          colorScheme={colorScheme}
          onClear={() => onChange(null)}
          onQueryChange={handleSearchChange}
          onRemove={handleToggle}
          options={chipOptions}
          panelContent={
            <Box p='12px'>
              <CheckboxFilterList
                autoFocusSearch
                colorScheme={colorScheme}
                emptyMessage={noOptionsText}
                filterMode={isExternalSearch ? 'external' : 'internal'}
                isLoading={isLoading ?? false}
                noMatchMessage={noOptionsText}
                onSearchChange={handleSearchChange}
                onSelectAll={handleSelectAll}
                onToggle={handleToggle}
                options={listOptions}
                preserveOptionOrder={preserveOptionOrder}
                searchPlaceholder={placeholder}
                searchValue={activeSearchValue}
                selectedValues={selectedKeys}
              />
            </Box>
          }
          placeholder={placeholder}
          returnFocusOnEscape
          selected={selectedKeys}
        />
      </Box>
    </FilterRow>
  )
}
